import { EyeOff } from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  clearImpersonationMeta,
  clearRememberedAccountId,
  clearStashedActorSession,
  readStashedActorSession,
} from "@/lib/impersonation";

/** 横幅高度，供 toast / 右上角浮层避让（与下方 py-2 文案行高大致一致） */
export const IMPERSONATION_BANNER_OFFSET_PX = 48;

/** 仅操作者本机可见；对方账号无登录痕迹。占位布局，不盖住面包屑。 */
export function ImpersonationBanner() {
  const { user, impersonation, setAuthUser, refreshUser } = useAuth();
  const navigate = useNavigate();

  if (!impersonation || !user || user.id !== impersonation.target_user_id) return null;

  async function exitImpersonation() {
    const stashed = readStashedActorSession();
    const targetRefresh = api.getRefreshToken();

    // 先吊销代登 refresh，避免残留；不写对方退出日志（后端对 imp 会话跳过）
    if (targetRefresh) {
      try {
        await api.fetch("/auth/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: targetRefresh }),
        });
      } catch {
        // ignore
      }
    }

    if (!stashed) {
      toast.error("无法恢复原管理员会话，请重新登录");
      clearImpersonationMeta();
      clearStashedActorSession();
      api.clearTokens();
      navigate("/login", { replace: true });
      return;
    }

    clearRememberedAccountId();
    clearImpersonationMeta();
    clearStashedActorSession();

    const expiresIn = stashed.expires_at
      ? Math.max(0, Math.floor((Number(stashed.expires_at) - Date.now()) / 1000))
      : undefined;

    api.setTokens({
      access_token: stashed.access_token,
      refresh_token: stashed.refresh_token,
      expires_in: expiresIn,
    });
    api.startTokenRefreshScheduler();

    try {
      const actor = await refreshUser();
      setAuthUser(actor);
      toast.success("已退出模拟登录");
      navigate("/platform/users", { replace: true });
    } catch {
      api.clearTokens();
      toast.error("原会话已失效，请重新登录");
      navigate("/login", { replace: true });
      window.location.reload();
    }
  }

  return (
    <div
      role="status"
      className="z-50 flex h-12 shrink-0 items-center justify-center gap-3 border-b border-amber-600/30 bg-amber-500 px-4 text-sm text-amber-950"
    >
      <EyeOff className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">
        正在隐身查看「{impersonation.target_label}」
        <span className="opacity-80">（对方不会看到登录记录）</span>
      </span>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-7 shrink-0 bg-amber-950 text-amber-50 hover:bg-amber-900"
        onClick={() => void exitImpersonation()}
      >
        退出模拟
      </Button>
    </div>
  );
}
